import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"
import { formatRange } from "./formatters"

const DefinitionContext = createContext(null)
const FOCUSABLE = "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])"

const DefinitionDetails = ({ definition, snapshot }) => (
  <div className="analytics-definition-details">
    <p className="analytics-definition-summary">{definition.summary}</p>
    <dl>
      <div><dt>Formula</dt><dd>{definition.formula}</dd></div>
      <div><dt>Included population</dt><dd>{definition.population}</dd></div>
      <div><dt>Exclusions</dt><dd>{definition.exclusions}</dd></div>
      <div><dt>Business Date</dt><dd>{definition.businessDate}</dd></div>
      <div><dt>Geography</dt><dd>{definition.geography}</dd></div>
      <div><dt>Comparison</dt><dd>{definition.comparison}</dd></div>
      <div><dt>Freshness</dt><dd>{definition.freshness}</dd></div>
      <div><dt>Quality reason</dt><dd>{definition.qualityReason}</dd></div>
    </dl>
    {snapshot?.ranges && (
      <div className="analytics-definition-ranges" aria-label="Definition reporting ranges">
        <span>Current <strong>{formatRange(snapshot.ranges.current)}</strong></span>
        <span>Comparison <strong>{formatRange(snapshot.ranges.comparison)}</strong></span>
      </div>
    )}
  </div>
)

export const DefinitionExperience = ({ snapshot, children }) => {
  const [selection, setSelection] = useState(null)
  const drawerRef = useRef(null)
  const returnFocusRef = useRef(null)
  const definitions = useMemo(() => snapshot?.definitions || [], [snapshot?.definitions])
  const byMetric = useMemo(
    () => new Map(definitions.map(definition => [definition.metricKey, definition])),
    [definitions]
  )

  const close = () => {
    returnFocusRef.current?.focus()
    setSelection(null)
  }

  const openMetric = (metric, trigger) => {
    const definition = byMetric.get(metric.key)
    if (!definition) return
    returnFocusRef.current = trigger
    setSelection({ type: "metric", definition })
  }

  const openDictionary = trigger => {
    returnFocusRef.current = trigger
    setSelection({ type: "dictionary" })
  }

  useEffect(() => {
    if (!selection) return undefined
    const drawer = drawerRef.current
    const focusable = () => [...(drawer?.querySelectorAll(FOCUSABLE) || [])]
    focusable()[0]?.focus()
    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault()
        close()
        return
      }
      if (event.key !== "Tab") return
      const items = focusable()
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [selection])

  return (
    <DefinitionContext.Provider value={{
      openMetric,
      openDictionary,
      hasDefinitions: definitions.length > 0,
      hasMetric: metric => byMetric.has(metric?.key),
    }}>
      {children}
      {selection && (
        <div className="analytics-definition-overlay" role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget) close()
        }}>
          <aside
            className="analytics-definition-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={selection.type === "dictionary"
              ? "KPI Dictionary"
              : `${selection.definition.label} definition`}
            ref={drawerRef}
          >
            <header>
              <div>
                <p className="analytics-eyebrow">Definition version {snapshot?.metadata?.definitionVersion || "unknown"}</p>
                <h2>{selection.type === "dictionary" ? "KPI Dictionary" : selection.definition.label}</h2>
              </div>
              <button type="button" aria-label="Close definitions" onClick={close}>×</button>
            </header>
            {selection.type === "dictionary" ? (
              <div className="analytics-dictionary-list">
                {definitions.length ? definitions.map(definition => (
                  <article key={`${definition.metricKey}:${definition.id}`}>
                    <h3>{definition.label}</h3>
                    <DefinitionDetails definition={definition} snapshot={snapshot} />
                  </article>
                )) : <p>No versioned definitions are available in this snapshot.</p>}
              </div>
            ) : <DefinitionDetails definition={selection.definition} snapshot={snapshot} />}
          </aside>
        </div>
      )}
    </DefinitionContext.Provider>
  )
}

export const DefinitionButton = ({ metric }) => {
  const context = useContext(DefinitionContext)
  if (!context?.hasMetric(metric)) return null
  return (
    <button
      type="button"
      className="analytics-definition-button"
      aria-label={`Definition: ${metric.label}`}
      onClick={event => context.openMetric(metric, event.currentTarget)}
    >
      Definition
    </button>
  )
}

export const DictionaryButton = () => {
  const context = useContext(DefinitionContext)
  if (!context?.hasDefinitions) return null
  return (
    <button
      type="button"
      className="analytics-dictionary-button"
      onClick={event => context.openDictionary(event.currentTarget)}
    >
      KPI Dictionary
    </button>
  )
}

export default DefinitionExperience
